import React, { useEffect, useState } from "react";
import "./AllAds.scss";
import {
  Button,
  Input,
  Tabs,
  Table,
  Image,
  Tag,
  Dropdown,
  Modal,
  DatePicker,
  Select,
} from "antd";
import type { TableColumnsType, TableProps } from "antd";
import {
  EllipsisOutlined,
  PlusOutlined,
  RetweetOutlined,
  SearchOutlined,
  StopOutlined,
} from "@ant-design/icons";
import Helpers from "@utils/Helpers";
import { useNavigate } from "react-router-dom";
import { errorLog } from "logging";
import { useAuth } from "contexts/AuthContext";
import { useUser } from "contexts/UserContext";
import { AdApi } from "services/AdApi";
import { MenuProps } from "antd";
import AdDetail from "@components/AdDetail/AdDetail";
import dayjs from "dayjs";

const AllAds: React.FC = () => {
  const [bottom] = useState<TablePaginationPosition<DataType>>("bottomRight");
  const navigate = useNavigate();
  const [allAd, setAllAd] = useState<any>(null);
  const [adType, setAdType] = useState<any>(null);
  const { isAuthenticated } = useAuth();
  const { userProfile } = useUser();
  const [selectedAd, setSelectedAd] = useState<any>(null);
  const [isOpenRepost, setIsOpenRepost] = useState(false);
  const [isOpenStop, setIsOpenStop] = useState(false);
  const [isAdDetail, setIsAdDetail] = useState(false);

  useEffect(() => {
    const fetchAllAds = async () => {
      try {
        const response = await AdApi.getAll();
        if (response.status === 200) {
          const myAds = response.data.data.filter(
            (ad: any) => ad.user_id === userProfile?.user_id
          );
          setAllAd(myAds);
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch data. Please try again");
        errorLog("CreateAd.tsx - fetchAllAds", error);
      }
    };
    if (isAuthenticated && !allAd && userProfile?.type_ids.includes(2)) {
      fetchAllAds();
    }
  }, [allAd]);

  useEffect(() => {
    const getAdType = async () => {
      try {
        const response = await AdApi.getType();
        if (response.status === 200) {
          setAdType(response.data.ad_types);
        }
      } catch (error) {
        Helpers.notification.error("Can not fetch data. Please try again");
        errorLog("CreateAd.tsx - getAdType", error);
      }
    };

    getAdType();
  }, []);

  interface DataType {
    key: React.Key;
    action: object;
    name: object;
    type: string;
    status: object;
    totalSpend: string;
    impression: number;
    clicks: number;
    ctr: string;
    cpc: string;
    sold: number;
    conversion: number;
    conversionRate: number;
    roa: number;
    revenue: number;
  }

  const columns: TableColumnsType<DataType> = [
    {
      title: "",
      width: "max-content",
      dataIndex: "action",
      key: "1",
      fixed: "left",
    },
    {
      title: "NAME",
      width: "max-content",
      dataIndex: "name",
      key: "2",
      fixed: "left",
    },
    {
      title: "TYPE",
      width: "max-content",
      dataIndex: "type",
      key: "3",
    },
    {
      title: "STATUS",
      width: "max-content",
      dataIndex: "status",
      key: "4",
    },
    {
      title: "TOTAL SPEND",
      width: "max-content",
      dataIndex: "totalSpend",
      key: "5",
    },
    {
      title: "IMPRESSIONS",
      width: "max-content",
      dataIndex: "impression",
      key: "6",
    },
    { title: "CLICKS", width: "max-content", dataIndex: "clicks", key: "7" },
    { title: "CTR", width: "max-content", dataIndex: "ctr", key: "8" },
    { title: "CPC", width: "max-content", dataIndex: "cpc", key: "9" },
    { title: "ITEMS SOLD", width: "max-content", dataIndex: "sold", key: "10" },
    {
      title: "CONVERSIONS",
      width: "max-content",
      dataIndex: "conversion",
      key: "11",
    },
    {
      title: "CONVERSION RATE",
      width: "max-content",
      dataIndex: "conversionRate",
      key: "12",
    },
    { title: "ROAS", width: "max-content", dataIndex: "roa", key: "13" },
    { title: "REVENUE", width: "max-content", dataIndex: "revenue", key: "14" },
  ];

  const Action: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div
          className="action-item"
          onClick={() => {
            setIsOpenRepost(true);
          }}
        >
          <RetweetOutlined /> Repost
        </div>
      ),
    },
    {
      key: "2",
      label:
        selectedAd?.ad_status.title === "Active" ||
        selectedAd?.ad_status.title === "Scheduled" ? (
          <div
            className="action-item"
            onClick={() => {
              setIsOpenStop(true);
            }}
          >
            <StopOutlined /> Stop
          </div>
        ) : null,
    },
  ];

  const dataSource: DataType[] = allAd?.map((item: any) => {
    return {
      key: item.product_ad_id,
      action: (
        <Dropdown
          menu={{ items: Action }}
          trigger={["click"]}
          placement="bottomLeft"
        >
          <EllipsisOutlined onClick={() => setSelectedAd(item)} />
        </Dropdown>
      ),
      name: (
        <div
          className="item"
          onClick={() => {
            setSelectedAd(item);
            setIsAdDetail(true);
          }}
        >
          <Image
            className="image"
            src={item.product.images[0]}
            preview={false}
          />
          <div className="name">
            {item.product.product_type_name}{" "}
            <span className="date">
              {Helpers.formatDateTime(item.start_time.slice(0, 10))}
            </span>
          </div>
        </div>
      ),
      type: adType?.find((type: any) => type.id === item.ad_type_id)?.name,
      status:
        item.ad_status.product_ad_status_id === 1 ? (
          <div className="status">
            <Tag color="success">Active</Tag>
          </div>
        ) : item.ad_status.product_ad_status_id === 2 ? (
          <div className="status">
            <Tag color="default">Scheduled</Tag>
            <div className="date">
              <span>
                {Helpers.formatDateTime(item.start_time.slice(0, 10))} -
              </span>
              <span>{Helpers.formatDateTime(item.end_time.slice(0, 10))}</span>
            </div>
          </div>
        ) : item.ad_status.product_ad_status_id === 4 ? (
          <div className="status">
            <Tag color="error">Stopped</Tag>
          </div>
        ) : (
          <div className="status">
            <Tag color="info">Finished</Tag>
          </div>
        ),
      totalSpend: "$3,937.5",
      impression: 250000,
      clicks: 8750,
      ctr: "3.5%",
      cpc: "$0",
      sold: 50,
      conversion: 50,
      conversionRate: 50,
      roa: 50,
      revenue: 50,
    };
  });

  type TablePagination<T extends object> = NonNullable<
    Exclude<TableProps<T>["pagination"], boolean>
  >;
  type TablePaginationPosition<T extends object> = NonNullable<
    TablePagination<T>["position"]
  >[number];

  const handleRepostAd = async () => {
    const body = {
      product_ad_id: selectedAd?.product_ad_id,
      start_time: selectedAd?.start_time,
      duration: selectedAd?.duration,
    };
    try {
      const response = await AdApi.repostAd(body);
      if (response.status === 201) {
        Helpers.showSuccessUpdated("Repost ad successfully!");
        setAllAd(null);
        setIsOpenRepost(false);
      }
    } catch (error) {
      Helpers.notification.error("Can not repost ad. Please try again");
      errorLog("AdDetail.tsx - handleRepostAd", error);
    }
  };

  const handleChangeStatus = async (action: string) => {
    const body = {
      product_ad_id: selectedAd?.product_ad_id,
      action: action,
      description: selectedAd?.last_action.description,
      start_time: selectedAd?.last_action.at,
    };
    try {
      const response = await AdApi.changeStatus(body);
      if (response.status === 200) {
        const responseData = response.data.data;
        allAd.forEach((item: any) => {
          if (item.product_ad_id === responseData.product_ad_id) {
            item.product_ad_id = responseData.product_ad_id;
            item.last_action.description = responseData.last_action.description;
            item.last_action.action = responseData.last_action.action_type;
            item.last_action.at = responseData.last_action.at;
            item.ad_status.product_ad_status_id =
              responseData.product_ad_status_id;
          }
        });
        setAllAd(allAd);
        setIsOpenStop(false);
        Helpers.showSuccessUpdated("Update ad status successfully!");
      }
    } catch (error) {
      Helpers.notification.error("Can not update ad status. Please try again");
      errorLog("AdDetail.tsx - handleChangeStatus", error);
    }
  };

  return (
    <>
      {isAdDetail ? (
        <AdDetail
          adData={selectedAd}
          adList={allAd}
          setAdList={setAllAd}
          adType={adType}
          isUpdated={isAdDetail}
          setIsUpdated={setIsAdDetail}
        />
      ) : (
        <div className="AllAds">
          <div className="title">All Ads</div>
          <div className="search-group">
            <Input
              size="large"
              variant="outlined"
              suffix={<SearchOutlined />}
              style={{ color: "#808080" }}
              placeholder="Search products"
            />
            <Button
              type="primary"
              size="large"
              className="create-btn"
              onClick={() => navigate("/profile?direct=ads-register")}
              icon={<PlusOutlined />}
            >
              {" "}
              Create new
            </Button>
          </div>
          <Tabs
            className="tabs"
            defaultActiveKey="1"
            items={[
              {
                key: "1",
                label: "All",
                children: (
                  <Table<DataType>
                    className="custom-table"
                    pagination={{ position: [bottom] }}
                    rowSelection={{ type: "radio" }}
                    columns={columns}
                    dataSource={dataSource}
                    scroll={{ x: "max-content" }}
                  />
                ),
              },
              {
                key: "2",
                label: "Scheduled",
                children: (
                  <Table<DataType>
                    className="custom-table"
                    pagination={{ position: [bottom] }}
                    rowSelection={{ type: "radio" }}
                    columns={columns}
                    dataSource={dataSource?.filter(
                      (item: any) =>
                        item.status?.props?.children?.props?.children ===
                        "Scheduled"
                    )}
                    scroll={{ x: "max-content" }}
                  />
                ),
              },
              {
                key: "3",
                label: "Active",
                children: (
                  <Table<DataType>
                    className="custom-table"
                    pagination={{ position: [bottom] }}
                    rowSelection={{ type: "radio" }}
                    columns={columns}
                    dataSource={dataSource?.filter(
                      (item: any) =>
                        item.status?.props?.children?.props?.children ===
                        "Active"
                    )}
                    scroll={{ x: "max-content" }}
                  />
                ),
              },
              {
                key: "4",
                label: "Finished",
                children: (
                  <Table<DataType>
                    className="custom-table"
                    pagination={{ position: [bottom] }}
                    rowSelection={{ type: "radio" }}
                    columns={columns}
                    dataSource={dataSource?.filter(
                      (item: any) =>
                        item.status?.props?.children?.props?.children ===
                        "Finished"
                    )}
                    scroll={{ x: "max-content" }}
                  />
                ),
              },
              {
                key: "5",
                label: "Deleted",
                children: (
                  <Table<DataType>
                    className="custom-table"
                    pagination={{ position: [bottom] }}
                    rowSelection={{ type: "radio" }}
                    columns={columns}
                    dataSource={dataSource?.filter(
                      (item: any) =>
                        item.status?.props?.children?.props?.children ===
                        "Deleted"
                    )}
                    scroll={{ x: "max-content" }}
                  />
                ),
              },
              {
                key: "6",
                disabled: true,
                label: (
                  <div className="available-slot">
                    Available ad slots{" "}
                    <Button
                      icon={<PlusOutlined />}
                      iconPosition="end"
                      onClick={() => navigate("/profile?direct=add-slot")}
                    >
                      0
                    </Button>
                  </div>
                ),
                children: "",
              },
            ]}
          />

          <Modal
            title="Repost Ad"
            open={isOpenRepost}
            onOk={handleRepostAd}
            className="repost-popup"
            onCancel={() => setIsOpenRepost(false)}
            okText="Repost"
            cancelButtonProps={{ type: "text" }}
          >
            <span className="sub-title">
              Select the duration for reposting the ad.
            </span>
            <div className="product-info">
              <div className="product-info-image">
                <img
                  src={selectedAd?.product.images[0]}
                  alt={selectedAd?.product.product_type_name}
                />
              </div>
              <div className="product-info-details">
                <span className="product-name">
                  {selectedAd?.product.product_type_name}
                </span>
                <span className="product-id">ID: {selectedAd?.product_id}</span>
              </div>
            </div>
            <div className="ad-info">
              <div className="ad-info-item">
                <span className="ad-info-label">Ads type</span>
                <div className="ad-info-content">
                  {
                    adType?.find(
                      (type: any) => type.id === selectedAd?.ad_type_id
                    )?.name
                  }
                </div>
              </div>

              <div className="ad-info-item">
                <span className="ad-info-label">Duration</span>
                <div className="ad-info-content">
                  <Select
                    className="ad-info-select"
                    value={selectedAd?.duration / 7 || 1}
                    variant="filled"
                    options={[
                      { label: "1 week", value: 1 },
                      { label: "2 weeks", value: 2 },
                      { label: "3 weeks", value: 3 },
                      { label: "4 weeks", value: 4 },
                    ]}
                    onChange={(value: any) => {
                      setSelectedAd({
                        ...selectedAd,
                        duration: value * 7,
                        end_time: dayjs(selectedAd?.start_time)
                          .add(value, "week")
                          .toISOString(),
                      });
                    }}
                  />
                </div>
              </div>

              <div className="ad-info-item">
                <span className="ad-info-label"></span>
                <div className="ad-info-content">
                  <DatePicker
                    className="ad-info-input"
                    size="large"
                    variant="filled"
                    style={{ width: "100%" }}
                    prefix="Start date"
                    disabled
                    value={dayjs()}
                  />

                  <DatePicker
                    className="ad-info-input"
                    size="large"
                    variant="filled"
                    style={{ width: "100%" }}
                    prefix="End date"
                    value={
                      selectedAd?.end_time ? dayjs(selectedAd.end_time) : null
                    }
                    disabled
                  />
                </div>
              </div>

              <div className="ad-info-item">
                <span className="ad-info-label">Slot for ads</span>
                <div className="ad-info-content">
                  <Input
                    disabled
                    className="ad-info-input"
                    size="large"
                    variant="filled"
                    value={selectedAd?.duration / 7}
                    suffix="slot"
                  />
                </div>
              </div>
            </div>
            <span className="sub-title">
              You are about to repost this ad. This will make the ad active
              again for the selected duration.
            </span>
          </Modal>

          <Modal
            title="Stop Ad"
            open={isOpenStop}
            onOk={() => handleChangeStatus("stop")}
            className="repost-popup"
            onCancel={() => setIsOpenStop(false)}
            okText="Stop"
            cancelButtonProps={{ type: "text" }}
          >
            <span className="sub-title">
              This will stop your ad from running and will remove it from the
              active list.
            </span>
            <div className="product-info">
              <div className="product-info-image">
                <img
                  src={selectedAd?.product.images[0]}
                  alt={selectedAd?.product.product_type_name}
                />
              </div>
              <div className="product-info-details">
                <span className="product-name">
                  {selectedAd?.product.product_type_name}
                </span>
                <span className="product-id">ID: {selectedAd?.product_id}</span>
              </div>
            </div>
            <div className="ad-info">
              <div className="ad-info-item">
                <span className="ad-info-label">Ads type</span>
                <div className="ad-info-content">
                  {
                    adType?.find(
                      (type: any) => type.id === selectedAd?.ad_type_id
                    )?.name
                  }
                </div>
              </div>

              <div className="ad-info-item">
                <span className="ad-info-label">Reason</span>
                <div className="ad-info-content">
                  <Select
                    className="ad-info-select"
                    value={selectedAd?.last_action.description}
                    variant="filled"
                    options={[
                      {
                        label: "Campaign ended early",
                        value: "Campaign ended early",
                      },
                      {
                        label: "Business strategy change",
                        value: "Business strategy change",
                      },
                      {
                        label: "No longer accepting orders",
                        value: "No longer accepting orders",
                      },
                      { label: "Others", value: "Others" },
                    ]}
                    onChange={(value: any) => {
                      setSelectedAd({
                        ...selectedAd,
                        last_action: {
                          ...selectedAd.last_action,
                          description: value,
                        },
                      });
                    }}
                  />
                </div>
              </div>

              <div className="ad-info-item">
                <span className="ad-info-label">Start date</span>
                <div className="ad-info-content">
                  <DatePicker
                    className="ad-info-input"
                    disabled
                    size="large"
                    variant="filled"
                    style={{ width: "100%" }}
                    value={dayjs()}
                  />
                </div>
              </div>
              
              <div className="ad-info-item">
                <span className="ad-info-label">End date</span>
                <div className="ad-info-content">
                  <DatePicker
                    className="ad-info-input"
                    disabled
                    size="large"
                    variant="filled"
                    style={{ width: "100%" }}
                    value={
                      selectedAd?.end_time ? dayjs(selectedAd.end_time) : null
                    }
                  />
                </div>
              </div>
            </div>
          </Modal>
        </div>
      )}
    </>
  );
};

export default AllAds;
